/**
 * Cloud Operations — Workflow canvas (read view).
 *
 * Self-contained SVG DAG renderer for a System Deployment Workflow's
 * definition. Used by:
 *   • SystemWorkflowCatalogPage — "View canvas" on each workflow
 *   • DeploymentDetailPage      — "Workflow" tab showing what the run executed
 *
 * Why a tiny custom renderer instead of the V3 Canvas component:
 *   • V3 Canvas is the AUTHORING surface (drag, drop, edit) — heavy and
 *     stateful. The audit view needs read-only legibility, not editing.
 *   • System Deployment Workflows are linear or near-linear chains today;
 *     a column-flow layout is enough.
 *   • Coupling to V3 would import edit dependencies into Cloud Operations
 *     and tie ship dates together.
 *
 * Layout: nodes arranged left-to-right in the order edges connect them.
 * Wrap to next row at MAX_COLS. Click a node → side panel with full props.
 *
 * Accepts: { definition } prop with shape:
 *   { nodes: [{ id, type, name, connector?, skill?, inputs?, retry_policy?, … }],
 *     connections | edges: [{ from, to } | { fromNodeId, toNodeId }] }
 */

import React, { useMemo, useState } from 'react';
import { T } from './_ui.jsx';

// ─── layout constants ─────────────────────────────────────────────────────
const NODE_W = 200;
const NODE_H = 70;
const COL_GAP = 64;
const ROW_GAP = 50;
const PADDING = 24;
const MAX_COLS = 4;

const TYPE_STYLES = {
  start_sentinel:   { bg: '#e8f0fb', border: '#1f5fa6', label: 'START',   accent: '#1f5fa6' },
  end_sentinel:     { bg: '#e7f4ec', border: '#2e7d4f', label: 'END',     accent: '#2e7d4f' },
  delay:            { bg: '#fff4d9', border: '#c58b00', label: 'DELAY',   accent: '#c58b00' },
  connector_skill:  { bg: '#ffffff', border: '#d4dbe5', label: 'SKILL',   accent: '#0e3552' },
  sub_workflow:     { bg: '#f3edf8', border: '#6b3f9e', label: 'SUB-WF',  accent: '#6b3f9e' },
};

export default function WorkflowCanvasReadView({ definition }) {
  const { nodes, edges } = useMemo(() => parseDefinition(definition), [definition]);
  const positions       = useMemo(() => layoutNodes(nodes, edges), [nodes, edges]);
  const [selectedId, setSelectedId] = useState(null);

  if (!nodes.length) {
    return (
      <div style={{
        background: T.bgCard, border: `1px dashed ${T.stroke}`,
        borderRadius: 10, padding: '40px 24px', textAlign: 'center', color: T.text2,
      }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>No nodes to render</div>
        <div style={{ fontSize: 12, marginTop: 4 }}>This workflow's definition has no nodes array.</div>
      </div>
    );
  }

  const { width, height } = bbox(positions);
  const selected = nodes.find((n) => n.id === selectedId);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 320px' : '1fr', gap: 16, alignItems: 'start' }}>
      <div style={{
        background: T.bgCard, border: `1px solid ${T.stroke}`, borderRadius: 10,
        padding: 12, overflowX: 'auto',
      }}>
        <CanvasSvg
          width={width}
          height={height}
          nodes={nodes}
          edges={edges}
          positions={positions}
          selectedId={selectedId}
          onSelect={setSelectedId}
        />
        <Legend />
      </div>

      {selected && (
        <NodeDetailPanel
          node={selected}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}

// ─── SVG canvas ───────────────────────────────────────────────────────────

function CanvasSvg({ width, height, nodes, edges, positions, selectedId, onSelect }) {
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      style={{ display: 'block', minWidth: '100%' }}
    >
      <defs>
        <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5"
                markerWidth="6" markerHeight="6" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" fill={T.text2} />
        </marker>
      </defs>

      {/* Edges drawn FIRST so node boxes overlay nicely. */}
      {edges.map((e, i) => {
        const a = positions[e.from], b = positions[e.to];
        if (!a || !b) return null;
        const path = edgePath(a, b);
        return (
          <path
            key={`e${i}`}
            d={path}
            fill="none"
            stroke={T.text2}
            strokeWidth={1.5}
            markerEnd="url(#arrow)"
          />
        );
      })}

      {/* Nodes. */}
      {nodes.map((n) => {
        const p = positions[n.id];
        if (!p) return null;
        const style = TYPE_STYLES[n.type] || { bg: '#fff', border: T.stroke, label: (n.type || '').toUpperCase(), accent: T.ink };
        const isSelected = selectedId === n.id;
        return (
          <g
            key={n.id}
            transform={`translate(${p.x}, ${p.y})`}
            style={{ cursor: 'pointer' }}
            onClick={() => onSelect(n.id)}
          >
            <rect
              width={NODE_W}
              height={NODE_H}
              rx={6}
              ry={6}
              fill={style.bg}
              stroke={isSelected ? T.action : style.border}
              strokeWidth={isSelected ? 2 : 1}
            />
            <text x={10} y={18} fontSize={10} fontWeight={700} fill={style.accent}
                  fontFamily="ui-sans-serif, system-ui">
              {style.label}
            </text>
            <text x={10} y={36} fontSize={12} fontWeight={600} fill={T.ink}
                  fontFamily="ui-sans-serif, system-ui">
              {truncate(n.name || n.id, 32)}
            </text>
            {(n.connector || n.skill) && (
              <text x={10} y={54} fontSize={10} fill={T.text2}
                    fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace">
                {truncate([n.connector, n.skill].filter(Boolean).join('.'), 36)}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function Legend() {
  const items = Object.entries(TYPE_STYLES);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 12, fontSize: 11, color: T.text2 }}>
      {items.map(([k, s]) => (
        <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{
            width: 12, height: 12, borderRadius: 3,
            background: s.bg, border: `1px solid ${s.border}`,
          }} />
          {k}
        </span>
      ))}
    </div>
  );
}

// ─── side panel ───────────────────────────────────────────────────────────

function NodeDetailPanel({ node, onClose }) {
  return (
    <div style={{
      background: T.bgCard, border: `1px solid ${T.stroke}`, borderRadius: 10,
      padding: 16, position: 'sticky', top: 16,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.text3, letterSpacing: '.05em', textTransform: 'uppercase' }}>
            {node.type || 'node'}
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.ink, marginTop: 2 }}>
            {node.name || node.id}
          </div>
        </div>
        <button onClick={onClose} style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          fontSize: 18, color: T.text2,
        }} title="Close">×</button>
      </div>

      <Field label="Node ID"><code style={{ fontSize: 12 }}>{node.id}</code></Field>

      {(node.connector || node.skill) && (
        <Field label="Skill">
          <code style={{ fontSize: 12 }}>{[node.connector, node.skill].filter(Boolean).join('.')}</code>
        </Field>
      )}

      {node.duration_ms != null && (
        <Field label="Duration">{node.duration_ms} ms</Field>
      )}

      {node.timeout_ms != null && (
        <Field label="Timeout">{node.timeout_ms} ms</Field>
      )}

      {node.retry_policy && (
        <Field label="Retry policy">
          <pre style={codePre}>{JSON.stringify(node.retry_policy, null, 2)}</pre>
        </Field>
      )}

      {node.inputs && Object.keys(node.inputs).length > 0 && (
        <Field label="Inputs">
          <pre style={codePre}>{JSON.stringify(node.inputs, null, 2)}</pre>
        </Field>
      )}

      {node.outputs && (
        <Field label="Outputs">
          <pre style={codePre}>{JSON.stringify(node.outputs, null, 2)}</pre>
        </Field>
      )}

      {node.rationale && (
        <Field label="Rationale">
          <div style={{ fontSize: 12, color: T.text2 }}>{node.rationale}</div>
        </Field>
      )}

      {node.idempotency_key && (
        <Field label="Idempotency key">
          <code style={{ fontSize: 11, wordBreak: 'break-all' }}>{node.idempotency_key}</code>
        </Field>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: T.text3, letterSpacing: '.05em', textTransform: 'uppercase', marginBottom: 4 }}>
        {label}
      </div>
      <div>{children}</div>
    </div>
  );
}

const codePre = {
  background: '#0a1d44', color: '#e8f0fb',
  padding: 10, borderRadius: 4,
  fontSize: 11, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  maxHeight: 200, overflow: 'auto',
  margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
};

// ─── parsing + layout helpers ─────────────────────────────────────────────

function parseDefinition(def) {
  if (!def || typeof def !== 'object') return { nodes: [], edges: [] };
  const rawNodes = Array.isArray(def.nodes) ? def.nodes : [];
  const rawEdges = Array.isArray(def.connections) ? def.connections
                 : Array.isArray(def.edges)        ? def.edges
                 : [];

  const nodes = rawNodes.map((n) => ({
    ...n,
    id:   n.id || `n_${Math.random().toString(36).slice(2, 7)}`,
    name: n.name || n.label || n.id,
  }));

  const edges = rawEdges.map((e) => ({
    from: e.from ?? e.fromNodeId,
    to:   e.to   ?? e.toNodeId,
  })).filter((e) => e.from && e.to);

  return { nodes, edges };
}

/**
 * Greedy left-to-right layout. We walk from start_sentinel along outgoing
 * edges, placing each node into the next column. Wraps to a new row at
 * MAX_COLS. Nodes the walk doesn't reach (orphans) are placed at the end.
 */
function layoutNodes(nodes, edges) {
  const positions = {};
  if (!nodes.length) return positions;

  const adj = new Map();
  edges.forEach((e) => {
    if (!adj.has(e.from)) adj.set(e.from, []);
    adj.get(e.from).push(e.to);
  });

  const visited = new Set();
  const queue = [];
  // Start at start_sentinel(s) or first node.
  const starts = nodes.filter((n) => n.type === 'start_sentinel');
  if (starts.length) starts.forEach((s) => queue.push(s.id));
  else queue.push(nodes[0].id);

  let col = 0, row = 0;
  while (queue.length) {
    const id = queue.shift();
    if (visited.has(id)) continue;
    visited.add(id);

    const x = PADDING + col * (NODE_W + COL_GAP);
    const y = PADDING + row * (NODE_H + ROW_GAP);
    positions[id] = { x, y };

    col++;
    if (col >= MAX_COLS) { col = 0; row++; }

    const succ = adj.get(id) || [];
    succ.forEach((s) => { if (!visited.has(s)) queue.push(s); });
  }

  // Place any orphans on their own row at the bottom.
  nodes.forEach((n) => {
    if (positions[n.id]) return;
    const x = PADDING + col * (NODE_W + COL_GAP);
    const y = PADDING + row * (NODE_H + ROW_GAP);
    positions[n.id] = { x, y };
    col++;
    if (col >= MAX_COLS) { col = 0; row++; }
  });

  return positions;
}

function bbox(positions) {
  let maxX = 0, maxY = 0;
  Object.values(positions).forEach((p) => {
    if (p.x + NODE_W > maxX) maxX = p.x + NODE_W;
    if (p.y + NODE_H > maxY) maxY = p.y + NODE_H;
  });
  return { width: maxX + PADDING, height: maxY + PADDING };
}

/**
 * Simple right-edge of source → left-edge of target. For same-row edges, a
 * straight line; for wrapped (next-row) edges, an L-shape via an intermediate
 * point so the arrow doesn't slash diagonally across other nodes.
 */
function edgePath(a, b) {
  const ax = a.x + NODE_W;
  const ay = a.y + NODE_H / 2;
  const bx = b.x;
  const by = b.y + NODE_H / 2;
  if (Math.abs(ay - by) < 4) {
    return `M ${ax} ${ay} L ${bx} ${by}`;
  }
  // L-shaped (wrap to next row): out the right edge, down, then in the left edge.
  const midX = ax + COL_GAP / 2;
  return `M ${ax} ${ay} L ${midX} ${ay} L ${midX} ${by} L ${bx} ${by}`;
}

function truncate(s, n) {
  if (!s) return '';
  return s.length <= n ? s : s.slice(0, n - 1) + '…';
}
